import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { BCP47, DEFAULT_LOCALE, isLocale, LOCALES } from '@/shared/i18n/locales';
import type { Locale } from '@/shared/i18n/locales';
import { resources } from '@/shared/i18n/messages';
import { installPluralRules } from '@/shared/i18n/plural-rules';

/**
 * L'instance i18next de l'application, prête dès l'import.
 *
 * Elle s'initialise **de façon synchrone** : les traductions sont empaquetées
 * dans le bundle, il n'y a rien à aller chercher. C'est ce qui permet à
 * `translate()` de servir depuis n'importe quel module — un intercepteur HTTP,
 * un libellé de statut — sans se demander si l'initialisation a eu lieu.
 *
 * Ce fichier ne dépend que de `locales`, `messages` et du complément de
 * pluriels : rien du reste de l'application. C'est ce qui évite le cycle qui
 * guette toujours l'i18n — les erreurs d'API se traduisent, et le module de
 * traduction ne doit pas avoir besoin du client d'API pour exister. La langue
 * choisie, elle, se pose depuis `index.ts`, qui sait lire le stockage.
 */
installPluralRules();

void i18next.use(initReactI18next).init({
  resources,
  // Provisoire : `initI18n()` pose la langue retenue dès le démarrage, avant le
  // premier écran. Le français plutôt qu'une détection ici, pour qu'un module
  // importé très tôt ne lise jamais une langue à moitié décidée.
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...LOCALES],
  defaultNS: 'common',
  interpolation: {
    // React échappe déjà tout ce qu'il affiche, et une apostrophe française
    // ressortirait en `&#39;` dans un `Text`.
    escapeValue: false,
  },
  returnNull: false,
  // i18next 25 publie au démarrage une annonce pour Locize dans la console :
  // du bruit au milieu des vrais messages, sans rapport avec BUL.
  showSupportNotice: false,
});

export const i18n = i18next;

/**
 * `t` hors composant.
 *
 * Pour les modules qui n'ont pas de rendu : le client HTTP, les libellés de
 * statut, les messages d'erreur construits loin de l'écran. Dans un composant,
 * on utilise `useTranslation()` — lui seul redessine l'écran quand la langue
 * change.
 */
export const translate = i18n.t.bind(i18n) as typeof i18n.t;

/** La langue affichée, ramenée à l'une des nôtres. */
export function currentLocale(): Locale {
  return isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : DEFAULT_LOCALE;
}

/**
 * Ce que porte l'en-tête `Accept-Language` de chaque requête.
 *
 * Le serveur répond aujourd'hui en français quoi qu'il arrive ; l'en-tête est
 * envoyé quand même, parce qu'il est la façon standard de le dire et qu'il ne
 * coûte rien. Le jour où l'API traduira ses refus, il n'y aura rien à changer
 * ici. En attendant, c'est `api-error` qui traduit ce qu'il peut, par code.
 */
export function acceptLanguage(): string {
  const locale = currentLocale();
  return `${BCP47[locale]},${locale};q=0.9`;
}
